import React, { useEffect, useMemo, useRef, useState } from 'react';
import './terminal.css';
import portfolioConfig from '../../config/portfolioConfig';

const line = (content) => ({ type: 'text', content });
const metaLine = (content) => ({ type: 'meta', content });
const linkLine = (label, href) => ({ type: 'link', label, href });
const spacer = { type: 'spacer' };

const Terminal = () => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef(null);
  const terminalRef = useRef(null);
  const { personal, projects, stackGroups, terminal } = portfolioConfig;

  const projectLookup = useMemo(() => {
    return projects.reduce((lookup, project, index) => {
      lookup[project.index] = { project, index };
      lookup[`${index + 1}`] = { project, index };
      lookup[project.id] = { project, index };
      lookup[project.title.toLowerCase()] = { project, index };
      lookup[project.label.toLowerCase()] = { project, index };
      return lookup;
    }, {});
  }, [projects]);

  const resolveProject = (args) => {
    const key = args.join(' ').trim().toLowerCase();
    return projectLookup[key]?.project || null;
  };

  const commands = {
    help: () => [
      line('Available commands'),
      metaLine('help       about      projects    project [name|number]'),
      metaLine('open [id]  status     stack       contact'),
      metaLine('whoami     ls         pwd         date       clear'),
      spacer
    ],
    about: () => [
      line(personal.name),
      metaLine(personal.role),
      spacer,
      line(personal.description),
      spacer
    ],
    projects: () => [
      line('Current projects'),
      ...projects.flatMap((project) => [
        metaLine(`${project.index}  ${project.title}`),
        line(`   ${project.label}`),
        line(`   ${project.status}`),
        spacer
      ])
    ],
    project: (args) => {
      const project = resolveProject(args);

      if (!project) {
        return [
          line('Project not found. Try: project 1'),
          spacer
        ];
      }

      return [
        line(project.title),
        metaLine(project.label),
        spacer,
        line(project.summary),
        line(project.inspectionNote),
        metaLine(project.technologies.join(' | ')),
        linkLine('Visit project', project.url),
        spacer
      ];
    },
    open: (args) => {
      const project = resolveProject(args);

      if (!project) {
        return [
          line('Project not found. Use "projects" to list current items.'),
          spacer
        ];
      }

      window.open(project.url, '_blank', 'noopener,noreferrer');
      return [
        line(`Opening ${project.title}`),
        linkLine(project.url, project.url),
        spacer
      ];
    },
    status: () => [
      line('Live status'),
      ...projects.flatMap((project) => [
        metaLine(`${project.title}: ${project.status}`),
        line(`   ${project.inspectionNote}`),
        spacer
      ])
    ],
    stack: () => [
      line('Core stack'),
      ...stackGroups.flatMap((group) => [
        metaLine(group.title),
        line(`   ${group.items.join(', ')}`),
        spacer
      ])
    ],
    contact: () => [
      line('Contact'),
      metaLine(personal.email),
      metaLine(personal.phone),
      metaLine(personal.location),
      spacer
    ],
    whoami: () => [line(personal.name), metaLine(personal.role), spacer],
    pwd: () => [metaLine(`${terminal.directory}/portfolio`), spacer],
    ls: () => [
      metaLine('about.md'),
      metaLine('current-projects.json'),
      metaLine('stack-notes.txt'),
      metaLine('contact.txt'),
      spacer
    ],
    date: () => [
      metaLine(
        new Intl.DateTimeFormat('en-IN', {
          dateStyle: 'full',
          timeStyle: 'long'
        }).format(new Date())
      ),
      spacer
    ],
    clear: () => {
      setHistory([]);
      return [];
    },
    echo: (args) => [line(args.join(' ')), spacer]
  };

  const executeCommand = (value) => {
    const [command, ...args] = value.trim().split(/\s+/);

    if (!command) {
      return [];
    }

    const handler = commands[command.toLowerCase()];
    if (handler) {
      return handler(args);
    }

    return [
      line(`Unknown command: ${command}`),
      metaLine('Type "help" to see available commands.'),
      spacer
    ];
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!input.trim()) {
      return;
    }

    const output = executeCommand(input);
    setHistory((currentHistory) => [
      ...currentHistory,
      { command: input, output, timestamp: new Date().toISOString() }
    ]);
    setCommandHistory((currentHistory) => [...currentHistory, input]);
    setHistoryIndex(-1);
    setInput('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (commandHistory.length > 0 && historyIndex < commandHistory.length - 1) {
        const nextIndex = historyIndex + 1;
        setHistoryIndex(nextIndex);
        setInput(commandHistory[commandHistory.length - 1 - nextIndex]);
      }
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (historyIndex > 0) {
        const nextIndex = historyIndex - 1;
        setHistoryIndex(nextIndex);
        setInput(commandHistory[commandHistory.length - 1 - nextIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
    }
  };

  useEffect(() => {
    setHistory([
      {
        command: 'welcome',
        output: [
          line('abhinav studio console'),
          metaLine('Type "help" to inspect the updated portfolio data.'),
          metaLine('Type "projects" to see the current three live project links.'),
          spacer
        ],
        timestamp: new Date().toISOString()
      }
    ]);
  }, []);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const quickCommands = ['help', 'projects', 'status', 'stack'];

  return (
    <section id="terminal" className="terminal-section section">
      <div className="container">
        <div className="terminal-section__intro">
          <div>
            <p className="section-eyebrow">Console</p>
            <h2>An interactive project terminal that now reflects the real work.</h2>
          </div>
          <p className="section-copy">
            The old terminal listed unrelated demo projects. This version is driven directly by
            the current project data, including live status notes and direct links.
          </p>
        </div>

        <div className="terminal-shell panel" onClick={() => inputRef.current?.focus()}>
          <div className="terminal-shell__bar">
            <div className="terminal-shell__lights">
              <span />
              <span />
              <span />
            </div>
            <p>
              {terminal.promptUser}:{terminal.directory}
            </p>
          </div>

          <div className="terminal-shell__body" ref={terminalRef}>
            {history.map((entry, entryIndex) => (
              <div key={`${entry.timestamp}-${entryIndex}`} className="terminal-entry">
                {entry.command !== 'welcome' && (
                  <div className="terminal-entry__command">
                    <span className="terminal-entry__prompt">
                      {terminal.promptUser}:{terminal.directory}$
                    </span>
                    <span>{entry.command}</span>
                  </div>
                )}

                {entry.output.map((outputLine, lineIndex) => {
                  if (outputLine.type === 'spacer') {
                    return <div key={`${entryIndex}-${lineIndex}`} className="terminal-entry__spacer" />;
                  }

                  if (outputLine.type === 'link') {
                    return (
                      <a
                        key={`${entryIndex}-${lineIndex}`}
                        href={outputLine.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="terminal-entry__link"
                      >
                        {outputLine.label}
                      </a>
                    );
                  }

                  return (
                    <div
                      key={`${entryIndex}-${lineIndex}`}
                      className={
                        outputLine.type === 'meta'
                          ? 'terminal-entry__line terminal-entry__line--meta'
                          : 'terminal-entry__line'
                      }
                    >
                      {outputLine.content}
                    </div>
                  );
                })}
              </div>
            ))}

            <form onSubmit={handleSubmit} className="terminal-entry__form">
              <label className="terminal-entry__command">
                <span className="terminal-entry__prompt">
                  {terminal.promptUser}:{terminal.directory}$
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={handleKeyDown}
                  className="terminal-entry__input"
                  spellCheck={false}
                />
              </label>
            </form>
          </div>

          <div className="terminal-shell__quick-actions">
            {quickCommands.map((command) => (
              <button
                key={command}
                type="button"
                onClick={() => {
                  setInput(command);
                  inputRef.current?.focus();
                }}
              >
                {command}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Terminal;
